import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

export const BOARDS_COLUMN_SEED_COMPLETED_EVENT = "boards_column_seed_completed";

/**
 * A Board Settings "Revalidate" click (app "Shared Boards 🔒" column) has finished: every orchestration
 * chunk job that click created is terminal. Emitted by ms-utils into the `user:<id>` room on
 * behalf of simb-orchestration's Boards column seed notifier.
 */
export interface BoardsColumnSeedCompletedEvent {
  userId: string;
  accountId?: string;
  requestId: string;
  boardId?: string;
  summary: {
    totalChunks: number;
    done: number;
    dead: number;
    syncGroups: number;
    boards: number;
    boardsWithColumn: number;
    written: number;
  };
  reason: "no_sync_group" | "no_column" | null;
  message: string;
}

interface UseBoardsColumnSeedStreamArgs {
  userId: string | number | undefined;
  accountId?: string | number;
  onCompleted: (event: BoardsColumnSeedCompletedEvent) => void;
  enabled?: boolean;
}

// Subscribes to the ms-utils notification socket and forwards Shared Boards column seed completions
// addressed to this user. Mirrors useItemLocationsSeedStream.
const useBoardsColumnSeedStream = ({
  userId,
  accountId,
  onCompleted,
  enabled = true,
}: UseBoardsColumnSeedStreamArgs) => {
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (!enabled || !userId) return;

    const url = `${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`;
    const socket: Socket = io(url);

    const isForThisUser = (incomingUserId: string | number | undefined): boolean =>
      String(incomingUserId) === String(userId);

    socket.on(BOARDS_COLUMN_SEED_COMPLETED_EVENT, (data: BoardsColumnSeedCompletedEvent) => {
      if (isForThisUser(data?.userId)) onCompletedRef.current(data);
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId, enabled]);
};

export default useBoardsColumnSeedStream;
